"use client";

import { Component, type ReactNode, type ErrorInfo } from "react";

interface ErrorBoundaryProps {
	children: ReactNode;
	fallback?: ReactNode;
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	resetKeys?: Array<string | number>;
}

interface ErrorBoundaryState {
	hasError: boolean;
	error?: Error;
}

export class ErrorBoundary extends Component<
	ErrorBoundaryProps,
	ErrorBoundaryState
> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error("ErrorBoundary caught error:", error, errorInfo);
		this.props.onError?.(error, errorInfo);
	}

	componentDidUpdate(prevProps: ErrorBoundaryProps): void {
		// Reset error boundary when resetKeys change
		if (this.state.hasError && this.props.resetKeys !== prevProps.resetKeys) {
			this.setState({ hasError: false, error: undefined });
		}
	}

	render(): ReactNode {
		if (this.state.hasError) {
			// Use custom fallback if provided
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default fallback UI
			return (
				<div className="flex flex-col items-center justify-center min-h-[200px] p-6 text-center">
					<div className="text-danger text-4xl mb-4">⚠️</div>
					<h2 className="text-xl font-semibold mb-2">Something went wrong</h2>
					<p className="text-default-500 mb-4">
						{this.state.error?.message || "An unexpected error occurred"}
					</p>
					<button
						type="button"
						onClick={() => this.setState({ hasError: false, error: undefined })}
						className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
					>
						Try again
					</button>
				</div>
			);
		}

		return this.props.children;
	}
}
